import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../shared/api/client';
import { useAuthStore } from '../shared/store/authStore';
import { enumLabel } from '../shared/i18n/enumLabel';
import { PageHeader } from '../shared/ui/PageHeader';
import { Card } from '../shared/ui/Card';
import { ParkingDataTable } from '../shared/ui/ParkingDataTable';
import { AppModal } from '../shared/ui/AppModal';
import { StatusBadge, statusToneFromEnum } from '../shared/ui/StatusBadge';

export function MaintenancePage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const canRead = useAuthStore((s) => s.hasPermission('maintenance:read'));
  const canWrite = useAuthStore((s) => s.hasPermission('maintenance:write'));
  const list = useQuery({
    queryKey: ['maintenance'],
    queryFn: () => api.listMaintenance(),
    enabled: canRead,
  });
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    title: '',
    assigneeName: '',
    scheduledAt: new Date().toISOString().slice(0, 16),
  });

  const create = useMutation({
    mutationFn: () =>
      api.createMaintenance({
        title: form.title,
        assigneeName: form.assigneeName || null,
        scheduledAt: new Date(form.scheduledAt).toISOString(),
      }),
    onSuccess: async () => {
      setOpen(false);
      await qc.invalidateQueries({ queryKey: ['maintenance'] });
    },
  });
  const complete = useMutation({
    mutationFn: (id: string) => api.completeMaintenance(id),
    onSuccess: async () => qc.invalidateQueries({ queryKey: ['maintenance'] }),
  });

  if (!canRead) {
    return <div className="alert alert-warning">{t('maintenance.forbidden')}</div>;
  }

  const rows = list.data?.data ?? [];
  const columns = [
    t('maintenance.titleCol'),
    t('maintenance.assignee'),
    t('maintenance.status'),
    t('maintenance.scheduled'),
    ...(canWrite ? [t('common.actions')] : []),
  ];

  return (
    <>
      <PageHeader
        actions={
          canWrite ? (
            <button type="button" className="btn btn-primary" onClick={() => setOpen(true)}>
              {t('maintenance.create')}
            </button>
          ) : undefined
        }
      />

      <Card>
        <ParkingDataTable
          storageKey="maintenance"
          columns={columns}
          refreshKey={rows.map((m) => `${m.id}:${m.status}`).join('|')}
          actionsColumn={canWrite}
          galleryItems={rows.map((m) => ({
            id: m.id,
            icon: 'bi-tools',
            iconTone: statusToneFromEnum(m.status),
            title: m.title,
            subtitle: m.assigneeName ?? '—',
            meta: new Date(m.scheduledAt).toLocaleString(),
            badge: (
              <StatusBadge tone={statusToneFromEnum(m.status)}>
                {enumLabel(t, 'maintenanceStatus', m.status)}
              </StatusBadge>
            ),
            actions:
              canWrite && m.status !== 'DONE' && m.status !== 'CANCELLED' ? (
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => complete.mutate(m.id)}
                >
                  {t('maintenance.complete')}
                </button>
              ) : undefined,
          }))}
        >
          {rows.map((m) => (
            <tr key={m.id}>
              <td className="fw-semibold">{m.title}</td>
              <td>{m.assigneeName ?? '—'}</td>
              <td>
                <StatusBadge tone={statusToneFromEnum(m.status)}>
                  {enumLabel(t, 'maintenanceStatus', m.status)}
                </StatusBadge>
              </td>
              <td>{new Date(m.scheduledAt).toLocaleString()}</td>
              {canWrite && (
                <td>
                  {m.status !== 'DONE' && m.status !== 'CANCELLED' && (
                    <button
                      type="button"
                      className="btn btn-sm btn-primary"
                      onClick={() => complete.mutate(m.id)}
                    >
                      {t('maintenance.complete')}
                    </button>
                  )}
                </td>
              )}
            </tr>
          ))}
        </ParkingDataTable>
      </Card>

      <AppModal
        open={open}
        onClose={() => setOpen(false)}
        title={t('maintenance.create')}
        footer={
          <>
            <button type="button" className="btn btn-light" onClick={() => setOpen(false)}>
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              disabled={!form.title || create.isPending}
              onClick={() => create.mutate()}
            >
              {t('common.save')}
            </button>
          </>
        }
      >
        <div className="d-grid gap-3">
          <div>
            <label className="form-label">{t('maintenance.titleCol')}</label>
            <input
              className="form-control"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">{t('maintenance.assignee')}</label>
            <input
              className="form-control"
              value={form.assigneeName}
              onChange={(e) => setForm({ ...form, assigneeName: e.target.value })}
            />
          </div>
          <div>
            <label className="form-label">{t('maintenance.scheduled')}</label>
            <input
              className="form-control"
              type="datetime-local"
              value={form.scheduledAt}
              onChange={(e) => setForm({ ...form, scheduledAt: e.target.value })}
            />
          </div>
        </div>
      </AppModal>
    </>
  );
}
